import { Inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";

import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { generateCityPath, generateCountryPath } from "@hermes/shop-utils";
import {
    ShopLocatorFacade,
    ShopLocatorState,
} from "@hermes/states/shop-locator";
import { RouterService } from "@hermes/utils-generic/services/router";

import {
    generateCountriesFromShops,
    navigateToCityPage,
    navigateToCountryPage,
    setCities,
    setCountries,
    setCurrentCity,
    setCurrentCountry,
    updateShopLocator,
} from "./shop-locations.actions";
import {
    selectCurrentCountry,
    selectCurrentLocation,
} from "./shop-locations.selectors";
import {
    CityEntry,
    CountryEntry,
    CurrentShopLocation,
    SHOP_LOCATIONS_CITY_MODE,
    SHOP_LOCATIONS_COUNTRY_MODE,
    SHOP_LOCATIONS_DEFAULT_MODE,
    SHOP_LOCATIONS_NO_SHOPS_MODE,
} from "./shop-locations.state";

@Injectable()
export class ShopLocationsEffects {
    public navigateToCountryPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToCountryPage),
                map(({ country }) => {
                    this.routerService.spaRedirect(
                        generateCountryPath(country.slugifiedCountry),
                    );
                }),
            ),
        { dispatch: false },
    );

    public navigateToCityPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(navigateToCityPage),
                withLatestFrom(
                    // When setCurrentCity is trigger, the current country is 100% defined
                    this.store.select(
                        selectCurrentCountry,
                    ) as Observable<CountryEntry>,
                ),
                map(([{ city }, { slugifiedCountry }]) => {
                    this.routerService.spaRedirect(
                        generateCityPath(slugifiedCountry, city.slugifiedCity),
                    );
                }),
            ),
        { dispatch: false },
    );
    /**
     * When a new country is selected, in ShopLocations feature,
     * we have to generate the list of cities associated with this country.
     *
     * We take as source data the shops already loaded in the ShopLocator feature.
     */
    public setCities$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setCurrentCountry),
            withLatestFrom(this.shopLocatorFacade.shopsWithIgnored$),
            map(([{ countryCode }, shops]) => {
                const citiesInCurrentCountry: CityEntry[] = Object.values(shops)
                    .filter(
                        (shop) =>
                            shop.url &&
                            shop.countryCode === countryCode &&
                            shop.city &&
                            shop.city !== "",
                    )
                    .map((shop) => ({
                        slugifiedCity: this.getSlugifiedCity(shop.url),
                        translatedCity: shop.city,
                    }));

                const cities = [
                    ...new Map(
                        citiesInCurrentCountry.map((entry) => [
                            entry["translatedCity"],
                            entry,
                        ]),
                    ).values(),
                ];

                return setCities({ cities });
            }),
        ),
    );

    public setCurrentCity$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(setCurrentCity),
                withLatestFrom(
                    // When setCurrentCity is trigger, the current country is 100% defined
                    this.store.select(
                        selectCurrentCountry,
                    ) as Observable<CountryEntry>,
                ),
                map(([{ city }, currentCountry]) => {
                    this.shopLocatorFacade.setSelectedCity(
                        currentCountry.countryCode,
                        city.translatedCity,
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * When the `generateCountriesFromShops` action is dispatch, we browse all the shops in the ShopLocator feature
     * to extract the country names and the country codes.
     *
     * We make each entry unique and save this list in the ShopLocations feature.
     */
    public generateCountriesFromShops$ = createEffect(() =>
        this.actions$.pipe(
            ofType(generateCountriesFromShops),
            withLatestFrom(this.shopLocatorFacade.shopsWithIgnored$),
            map(([, shops]) => {
                const countriesWithCountryCode: CountryEntry[] = Object.values(
                    shops,
                )
                    .filter((shop) => shop.url)
                    .map((shop) => {
                        const slugifiedCountry = this.getSlugifiedCountry(
                            shop.url,
                        );
                        const path = generateCountryPath(slugifiedCountry);
                        return {
                            continentName: shop.continent,
                            translatedCountry: shop.country,
                            countryCode: shop.countryCode,
                            slugifiedCountry,
                            path,
                        };
                    });

                const alphabeticallySortedCountries = [
                    ...new Map(
                        countriesWithCountryCode.map((entry) => [
                            entry["countryCode"],
                            entry,
                        ]),
                    ).values(),
                ].sort((a, b) =>
                    a.translatedCountry.localeCompare(b.translatedCountry),
                );

                return setCountries({
                    countries: alphabeticallySortedCountries,
                });
            }),
        ),
    );

    public updateShopLocator$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateShopLocator),
                switchMap(() => this.store.select(selectCurrentLocation)),
                filter(
                    (currentLocation) =>
                        currentLocation.pageMode !== "uninitialized",
                ),
                map(
                    (currentLocation) => currentLocation as CurrentShopLocation,
                ),
                tap((currentLocation) => {
                    switch (currentLocation.pageMode) {
                        case SHOP_LOCATIONS_CITY_MODE: {
                            this.shopLocatorFacade.setSelectedCity(
                                currentLocation.currentCountry.countryCode,
                                currentLocation.currentCity.translatedCity,
                            );

                            break;
                        }
                        case SHOP_LOCATIONS_COUNTRY_MODE:
                        case SHOP_LOCATIONS_DEFAULT_MODE: {
                            this.shopLocatorFacade.setSelectedCountry(
                                currentLocation.currentCountry.countryCode,
                            );

                            break;
                        }
                        case SHOP_LOCATIONS_NO_SHOPS_MODE: {
                            this.shopLocatorFacade.setSelectedCountry(
                                this.locale.countryCode,
                            );

                            break;
                        }
                        // No default
                    }
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private store: Store<ShopLocatorState>,
        private shopLocatorFacade: ShopLocatorFacade,
        private routerService: RouterService,
        @Inject(LOCALE) private locale: Locale,
    ) {}

    /**
     * URL format : find-store/francia/paris/hermes-paris-faubourg-saint-honore-DDB38Q6R/
     */
    private getSlugifiedCountry(url: string): string {
        const trimmedURL = url.split("/");
        return trimmedURL[1];
    }

    /**
     * URL format : find-store/francia/paris/hermes-paris-faubourg-saint-honore-DDB38Q6R/
     */
    private getSlugifiedCity(url: string): string {
        const trimmedURL = url.split("/");
        return trimmedURL[2];
    }
}
